import { useEffect, useRef, useState, useTransition, type ReactNode } from "react";
import { useBlocker } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import type { Party } from "@/lib/types";
import { CustomerPicker } from "./CustomerPicker";
import { LocationPicker, type ActivityPlace } from "./LocationPicker";
import { RequirementField, StopRequirementField, useConsignmentRequirements } from "./RequirementPicker";
import { ORDER_PRIORITIES, PRIORITY_LABEL_KEYS, formatActivityDate } from "./orderLabels";
import type { OrderPriority } from "./orders.api";
import type { LocationListItem } from "@/features/locations/locations.api";
import { listWarehouses, warehouseKeys } from "@/features/warehouse/warehouse.api";
import {
  createOrderListTemplate,
  deleteOrderListTemplate,
  readSampleFile,
  updateOrderListTemplate,
  type MailLists,
  type OrderListTemplate,
  type StandardStopView,
} from "./orderListTemplates.api";
import { intakeKeys, listIntakeSamples, readIntakeLists } from "@/features/intake/intake.api";
import { bestList, guessFields, metresText, parseNumber, prefixFor, rowsOfList, type FieldMap, type ListField, type ListRow, type PlaceRuleView, type PlaceRulesView } from "./listRows";
import { carriersOf, carriersText, weekAfter, whenOfSide } from "./OrderMailLists";

// An ordermail, new or changed — on the relation card and in Beheer, by an admin only (docs/invoerbak.md §3b). Left the
// ordermail itself: whose mail, how WLX reads it, and for a list its example. Right the order every mail of it becomes
// (Camiel, 25-10-2026: "juist een voorbeeld willen genereren rechts van het scherm. Alle relevante ordervelden moeten dan
// gewijzigd kunnen worden"), and per field where its value comes from: from the file (which column) or standard (what is
// typed here) — "je moet daar eigenlijk de mapping in het scherm maken". What the file or the mail says goes first; the
// standard fills in what it does not. Below it, plainly, whether a warehouse order for Scoretrace comes with it.

interface StopDraft {
  place: ActivityPlace | null;
  reference: string;
  instruction: string;
  contactName: string;
  contactPhone: string;
  requirements: string[];
}

interface StandardDraft {
  priority: OrderPriority;
  description: string;
  remark: string;
  loadingMeters: string;
  requirementIds: string[];
  load: StopDraft;
  unload: StopDraft;
}

function stopDraftOf(stop: StandardStopView | undefined): StopDraft {
  return {
    // The picker shows a location by its name and city; the rest of it is not needed here.
    place: stop?.location ? { kind: "location", location: { ...stop.location, city: stop.location.city ?? "" } as unknown as LocationListItem } : null,
    reference: stop?.reference ?? "",
    instruction: stop?.instruction ?? "",
    contactName: stop?.contactName ?? "",
    contactPhone: stop?.contactPhone ?? "",
    requirements: stop?.requirements ?? [],
  };
}

const orNull = (text: string) => (text.trim() ? text.trim() : null);

/** A place rule as the screen edits it: the column, and per text a place of the address book. */
interface RuleDraft {
  header: string;
  places: { contains: string; place: ActivityPlace | null }[];
}

/** Where the place of a stop comes from: one fixed place, the address columns of each row, or a text in a column. */
type PlaceSource = "standard" | "file" | "text";

function ruleDraftOf(rule: PlaceRuleView | undefined): RuleDraft {
  if (!rule) return { header: "", places: [{ contains: "", place: null }] };
  return {
    header: rule.header,
    places: rule.places.map((one) => ({
      contains: one.contains,
      place: one.location ? { kind: "location", location: { ...one.location, city: one.location.city ?? "" } as unknown as LocationListItem } : null,
    })),
  };
}

/** The texts of a rule that have a place of the address book. */
const rulePlaces = (rule: RuleDraft) =>
  rule.places.flatMap((one) => (one.contains.trim() && one.place?.kind === "location" ? [{ contains: one.contains.trim(), location: one.place.location }] : []));

function stopInput(stop: StopDraft) {
  return {
    locationId: stop.place?.kind === "location" ? stop.place.location.id : null,
    reference: orNull(stop.reference),
    instruction: orNull(stop.instruction),
    contactName: orNull(stop.contactName),
    contactPhone: orNull(stop.contactPhone),
    requirements: stop.requirements,
  };
}

const PLACE_FIELDS = { load: ["loadName", "loadStreet", "loadPostalCode", "loadCity", "loadCountry"], unload: ["unloadName", "unloadStreet", "unloadPostalCode", "unloadCity", "unloadCountry"] } as const;

export function OrdermailForm({
  current,
  party: givenParty = null,
  onClose,
  onSaved,
  onDeleted,
}: {
  current: OrderListTemplate | undefined;
  /** The customer, when it is made on a relation card. */
  party?: Party | null;
  onClose: () => void;
  onSaved: (template: OrderListTemplate) => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(current?.name ?? "");
  const [party, setParty] = useState<Party | null>(givenParty);
  const [readAs, setReadAs] = useState<"list" | "mail">(current?.readAs ?? "list");
  const [senders, setSenders] = useState((current?.senders ?? []).join(", "));
  const [subjectFilter, setSubjectFilter] = useState(current?.subjectFilter ?? "");
  const [autoIntake, setAutoIntake] = useState(current?.autoIntake ?? false);
  const [proposeWarehouse, setProposeWarehouse] = useState(current?.proposeWarehouse ?? false);
  const [prefix, setPrefix] = useState(current?.referencePrefix ?? "");
  const [perPallet, setPerPallet] = useState(metresText(current?.metresPerPallet ?? null));
  const [carriers, setCarriers] = useState(carriersText(current?.metresByCarrier ?? {}));
  const [loadDateRule, setLoadDateRule] = useState<"" | "previous_working_day">(current?.loadDateRule ?? "");
  const [filterHeader, setFilterHeader] = useState(current?.rowFilter?.header ?? "");
  const [filterText, setFilterText] = useState(current?.rowFilter?.contains ?? "");
  const [provisional, setProvisional] = useState(current?.provisional ?? false);
  const [noInvoice, setNoInvoice] = useState(current ? current.noInvoiceReason !== null : false);
  const [noInvoiceReason, setNoInvoiceReason] = useState(current?.noInvoiceReason ?? t("orders.repeatNoInvoiceDefault"));
  const [fields, setFields] = useState<FieldMap>(current?.fields ?? {});
  const [standard, setStandard] = useState<StandardDraft>(() => ({
    priority: current?.standard.priority ?? "regular",
    description: current?.standard.description ?? "",
    remark: current?.standard.remark ?? "",
    loadingMeters: metresText(current?.standard.loadingMeters ?? null),
    requirementIds: current?.standard.requirementIds ?? [],
    load: stopDraftOf(current?.standard.load),
    unload: stopDraftOf(current?.standard.unload),
  }));
  // The sender in box 1 of the CMR (Stockmeier: its own address in Ridderkerk, not Haps where it loads); sent only once
  // changed, so a sender an older ordermail took from its order stays.
  const [shipper, setShipper] = useState<ActivityPlace | null>(() => {
    const kept = current?.standard.shipper;
    if (!kept) return null;
    if (kept.locationId) return { kind: "location", location: { id: kept.locationId, name: kept.name, city: kept.city ?? "" } as unknown as LocationListItem };
    return { kind: "address", address: { name: kept.name, street: "", houseNumber: "", houseNumberAddition: "", postalCode: "", city: kept.city ?? "", countryCode: "" } };
  });
  const [shipperChanged, setShipperChanged] = useState(false);
  // The place of a stop per row from the file (IDP), one fixed place, or by a text in a column with the fixed place for
  // the rest (Lattiz: Weerts, else Haps).
  const [placeSource, setPlaceSource] = useState<Record<"load" | "unload", PlaceSource>>(() => {
    const sourceOf = (side: "load" | "unload"): PlaceSource =>
      current?.placeRules?.[side] ? "text" : current?.fields[`${side}Name`] || current?.fields[`${side}City`] || current?.fields[`${side}PostalCode`] ? "file" : "standard";
    return { load: sourceOf("load"), unload: sourceOf("unload") };
  });
  const placeFromFile = { load: placeSource.load === "file", unload: placeSource.unload === "file" };
  const [rules, setRules] = useState<Record<"load" | "unload", RuleDraft>>(() => ({ load: ruleDraftOf(current?.placeRules?.load), unload: ruleDraftOf(current?.placeRules?.unload) }));
  const setRule = (side: "load" | "unload", patch: (rule: RuleDraft) => RuleDraft) => setRules((now) => ({ ...now, [side]: patch(now[side]) }));
  /** The rules in use: a side whose place comes by a text, with a column and a text with a place. */
  const usedRules = (["load", "unload"] as const).flatMap((side) =>
    readAs === "list" && placeSource[side] === "text" && rules[side].header && rulePlaces(rules[side]).length > 0 ? [{ side, header: rules[side].header, places: rulePlaces(rules[side]) }] : [],
  );
  const rulesView: PlaceRulesView = Object.fromEntries(
    usedRules.map((rule) => [rule.side, { header: rule.header, places: rule.places.map((one) => ({ contains: one.contains, location: { id: one.location.id, name: one.location.name, city: one.location.city ?? null } })) }]),
  );
  const [deleting, setDeleting] = useState(false);
  // Only the fields this ordermail uses, the rest behind Meer velden (Camiel, 27-10-2026: of some 25 fields Lattiz uses six).
  const [allFields, setAllFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const known = useConsignmentRequirements().data ?? [];
  const warehouses = useQuery({ queryKey: warehouseKeys.warehouses, queryFn: listWarehouses, retry: false });

  const isList = readAs === "list";
  const partyId = current?.party.id ?? party?.id ?? null;
  const customerName = current?.party.name ?? party?.name ?? "—";

  // ─── The example ─────────────────────────────────────────────────────────
  // A mail of this customer from the bak, or a file of its own (CSV, Excel; a pdf is no list). Kept nowhere: the
  // columns are kept by their header.
  const [sampleId, setSampleId] = useState("");
  const [sampleKey, setSampleKey] = useState<string | null>(null);
  const [fileSample, setFileSample] = useState<MailLists | null>(null);
  const [fileNote, setFileNote] = useState<string | null>(null);
  const samples = useQuery({ queryKey: intakeKeys.samples(partyId ?? ""), queryFn: () => listIntakeSamples(partyId!), enabled: isList && partyId !== null });
  const sample = useQuery({ queryKey: intakeKeys.lists(sampleId), queryFn: () => readIntakeLists(sampleId), enabled: isList && sampleId !== "" });
  const sampleData = fileSample ?? (sampleId ? (sample.data ?? null) : null);
  // Not chosen: the table whose header holds the ordermail's columns as saved (not as being changed, or it would jump).
  const sampleList = sampleData ? (sampleData.lists.find((one) => one.key === sampleKey) ?? bestList(sampleData.lists, current?.fields ?? {})) : null;
  function chooseFile(file: File | undefined) {
    setFileNote(null);
    setFileSample(null);
    setSampleKey(null);
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".pdf")) {
      setFileNote(t("orders.mailChannelSamplePdf"));
      return;
    }
    setSampleId("");
    readSampleFile(file)
      .then((read) => (read.lists.length > 0 ? setFileSample(read) : setFileNote(t("orders.mailChannelSampleUnread", { file: file.name }))))
      .catch((err: unknown) => setFileNote(err instanceof Error ? err.message : t("common.errorGeneric")));
  }
  useEffect(() => {
    if (sampleList) setFields((now) => (Object.keys(now).length > 0 ? now : guessFields(sampleList.header)));
  }, [sampleList]);
  const headers = [...new Set([...(sampleList?.header ?? []), ...Object.values(fields).filter((header): header is string => Boolean(header))])];
  const sampleWeek = sampleData ? (sampleData.dates.week ?? weekAfter(sampleData.mail.receivedAt)) : "";
  const metresPerPallet = parseNumber(perPallet);
  const rows = sampleList
    ? rowsOfList(sampleList, {
        fields,
        loadDate: sampleData?.dates.load ?? null,
        unloadDate: sampleData?.dates.unload ?? null,
        week: sampleWeek,
        prefix: prefixFor(prefix.trim() || null, sampleWeek),
        metresPerPallet,
        metresByCarrier: carriersOf(carriers),
        loadDateRule: loadDateRule || null,
        rowFilter: filterHeader && filterText.trim() ? { header: filterHeader, contains: filterText.trim() } : null,
        placeRules: rulesView,
      })
    : [];
  const first = rows.find((row) => !row.filtered) ?? null;
  /** Where a row of the example loads or unloads: the place its text names, its own address, or the ordermail's. */
  const placeOfRow = (row: ListRow, side: "load" | "unload"): string | null => {
    const ruled = row.ruled[side];
    if (ruled) return ruled.name;
    if (placeFromFile[side]) return row.input[side].place?.name || null;
    const place = standard[side].place;
    return place?.kind === "location" ? place.location.name : place?.address.name || null;
  };
  /** By a text in a column: how many rows of the example go to each place, not only the first (Camiel, 26-10-2026). */
  const countsByPlace = (side: "load" | "unload") => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (row.filtered) continue;
      const place = placeOfRow(row, side) ?? t("orders.ordermailNoPlace");
      counts.set(place, (counts.get(place) ?? 0) + 1);
    }
    return [...counts].map(([place, count]) => t("orders.ordermailRowsTo", { count, place })).join(" · ");
  };
  /** What the first row of the example has in this column. */
  const cellOf = (header: string | undefined) => {
    if (!header || !sampleList || !first) return null;
    const column = sampleList.header.indexOf(header);
    return column >= 0 ? (sampleList.rows[first.index]?.[column]?.text ?? null) : null;
  };

  const setField = (field: ListField, header: string) =>
    setFields((now) => {
      const next = { ...now };
      if (header) next[field] = header;
      else delete next[field];
      return next;
    });
  const setStop = (side: "load" | "unload", patch: Partial<StopDraft>) => setStandard((now) => ({ ...now, [side]: { ...now[side], ...patch } }));

  // ─── Scoretrace ──────────────────────────────────────────────────────────
  const warehouseAt = (place: ActivityPlace | null) =>
    place?.kind === "location" ? (warehouses.data?.warehouses.find((one) => one.location?.id === place.location.id) ?? null) : null;
  const loadWarehouse = placeFromFile.load ? null : warehouseAt(standard.load.place);
  const unloadWarehouse = placeFromFile.unload ? null : warehouseAt(standard.unload.place);

  const ready = name.trim().length >= 2 && partyId !== null && (!noInvoice || noInvoiceReason.trim() !== "");

  function message(err: unknown): string {
    if (err instanceof ApiError && err.code === "TEMPLATE_NAME_TAKEN") return t("orders.listTemplateNameTaken");
    if (err instanceof ApiError && err.code === "PARTY_REQUIRED") return t("orders.mailChannelPartyRequired");
    if (err instanceof ApiError && err.code === "LOCATION_NOT_FOUND") return t("orders.ordermailPlaceGone");
    if (err instanceof ApiError && err.code === "VALIDATION_ERROR") return t("orders.mailChannelInvalid");
    return err instanceof Error ? err.message : t("common.errorGeneric");
  }

  /** What is saved, as it stands now: also what tells a change that is not saved yet. */
  function inputOf() {
    // A column chosen for a field: its standard is not kept beside it, so nothing works out of sight.
    const kept: FieldMap = { ...fields };
    for (const side of ["load", "unload"] as const) if (!placeFromFile[side]) for (const field of PLACE_FIELDS[side]) delete kept[field];
    const input = {
      name: name.trim(),
      readAs,
      senders: senders
        .split(/[,;\s]+/)
        .map((one) => one.trim())
        .filter(Boolean),
      subjectFilter: subjectFilter.trim() || null,
      autoIntake,
      proposeWarehouse,
      referencePrefix: prefix.trim() || null,
      metresPerPallet,
      metresByCarrier: carriersOf(carriers),
      loadDateRule: loadDateRule || null,
      rowFilter: filterHeader && filterText.trim() ? { header: filterHeader, contains: filterText.trim() } : null,
      placeRules: Object.fromEntries(usedRules.map((rule) => [rule.side, { header: rule.header, places: rule.places.map((one) => ({ contains: one.contains, locationId: one.location.id })) }])),
      provisional,
      noInvoiceReason: noInvoice ? noInvoiceReason.trim() : null,
      ...(isList ? { fields: kept } : {}),
      standard: {
        priority: standard.priority,
        description: isList && kept.description ? null : orNull(standard.description),
        remark: isList && kept.remark ? null : orNull(standard.remark),
        // A list has its metres per pallet; the mail the model reads may say none (Nutrifeed only says the kilos).
        ...(isList ? {} : { loadingMeters: parseNumber(standard.loadingMeters) }),
        requirementIds: standard.requirementIds,
        load: { ...stopInput(standard.load), ...(placeFromFile.load ? { locationId: null } : {}), ...(isList && kept.loadReference ? { reference: null } : {}), ...(isList && kept.loadInstruction ? { instruction: null } : {}) },
        unload: {
          ...stopInput(standard.unload),
          ...(placeFromFile.unload ? { locationId: null } : {}),
          ...(isList && kept.unloadReference ? { reference: null } : {}),
          ...(isList && kept.unloadInstruction ? { instruction: null } : {}),
        },
        ...(shipperChanged ? { shipperLocationId: shipper?.kind === "location" ? shipper.location.id : null } : {}),
      },
    };
    return input;
  }

  // Leaving with changes that are not saved asks first (Camiel, 26-10-2026: "als ik ergens anders klik terwijl ik de
  // mapping aan het maken ben wordt het niet opgeslagen maar ben ik alles kwijt. hij moet dan vragen om op te slaan"):
  // another page, another tab of the relation card, closing the window, or Annuleren.
  const now = JSON.stringify(inputOf());
  const initial = useRef<string | null>(null);
  if (initial.current === null) initial.current = now;
  const dirty = now !== initial.current;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const leaving = useRef(false);
  const [asking, setAsking] = useState(false);
  const blocker = useBlocker({
    shouldBlockFn: ({ current: here, next }) =>
      !leaving.current && dirtyRef.current && (next.pathname !== here.pathname || JSON.stringify(next.search) !== JSON.stringify(here.search)),
    enableBeforeUnload: () => !leaving.current && dirtyRef.current,
    withResolver: true,
  });

  /** Saves it; the template, or null when it was refused (the reason shows). */
  async function persist(): Promise<OrderListTemplate | null> {
    setError(null);
    const input = inputOf();
    try {
      return current ? await updateOrderListTemplate(current.id, input) : await createOrderListTemplate({ ...input, ...(party ? { partyId: party.id } : {}) });
    } catch (err) {
      setError(message(err));
      return null;
    }
  }

  function save() {
    startTransition(async () => {
      const saved = await persist();
      if (saved) {
        leaving.current = true;
        onSaved(saved);
      }
    });
  }

  function close() {
    if (dirty) setAsking(true);
    else onClose();
  }

  function remove() {
    if (!current) return;
    startTransition(async () => {
      try {
        await deleteOrderListTemplate(current.id);
        onDeleted();
      } catch (err) {
        setError(message(err));
      }
    });
  }

  // ─── The rows of the example order ───────────────────────────────────────
  /** The fields left out this render, by their label: what Meer velden holds. */
  const hiddenFields: string[] = [];
  /** Whether a field shows: when it is used — a column, a standard, a place — or when all fields are asked for. */
  const shows = (label: string, used: boolean) => {
    if (used || allFields) return true;
    hiddenFields.push(label);
    return false;
  };
  /** A field the file fills or the standard: the source, the standard when that is it, and the example's value. */
  const sourced = (field: ListField, standardInput: ReactNode | null, filled = false) => {
    const header = fields[field];
    const fromFile = isList && Boolean(header);
    const label = t(`orders.mailField.${field}` as never);
    if (!shows(label, fromFile || filled)) return null;
    return (
      <SourceRow
        key={field}
        label={label}
        source={
          isList ? (
            <ColumnSelect id={`ordermail-${field}-source`} value={header ?? ""} headers={headers} noneLabel={standardInput ? t("orders.ordermailSourceStandard") : t("orders.ordermailSourceNone")} onChange={(value) => setField(field, value)} />
          ) : (
            <span className="t-caption t-muted">{standardInput ? t("orders.ordermailSourceMailOrStandard") : t("orders.ordermailSourceMail")}</span>
          )
        }
        value={fromFile ? null : standardInput}
        example={fromFile ? cellOf(header) : null}
      />
    );
  };
  const textInput = (id: string, value: string, onChange: (text: string) => void, max = 200, placeholder?: string) => (
    <input id={id} className="wl-input" value={value} maxLength={max} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  );

  const stopRows = (side: "load" | "unload") => {
    const stop = standard[side];
    const fromFile = placeFromFile[side];
    const byText = isList && placeSource[side] === "text";
    const rule = rules[side];
    const placeFields = PLACE_FIELDS[side];
    const groupLabel = t(side === "load" ? "orders.mailGroupLoad" : "orders.mailGroupUnload");
    const fixedPicker = (
      <div className="wl-vstack" style={{ gap: "2px", alignItems: "stretch" }}>
        <LocationPicker id={`ordermail-${side}-place`} ariaLabel={t("orders.ordermailPlace")} value={stop.place} onChange={(place) => setStop(side, { place })} />
        {stop.place?.kind === "address" && <span className="t-caption" style={{ color: "var(--text-danger)" }}>{t("orders.ordermailPlaceBook")}</span>}
        {!stop.place && <span className="t-caption t-muted">{side === "load" ? t("orders.ordermailPlaceNoneLoad") : t("orders.ordermailPlaceNoneUnload")}</span>}
      </div>
    );
    const setRulePlace = (i: number, patch: Partial<RuleDraft["places"][number]>) =>
      setRule(side, (now) => ({ ...now, places: now.places.map((one, j) => (j === i ? { ...one, ...patch } : one)) }));
    return (
      <>
        <SourceRow
          label={t("orders.ordermailPlace")}
          source={
            isList ? (
              <select
                id={`ordermail-${side}-place-source`}
                className="wl-select"
                value={placeSource[side]}
                onChange={(e) => setPlaceSource((now) => ({ ...now, [side]: e.target.value as PlaceSource }))}
              >
                <option value="standard">{t("orders.ordermailPlaceStandard")}</option>
                <option value="file">{t("orders.ordermailPlaceFile")}</option>
                <option value="text">{t("orders.ordermailPlaceText")}</option>
              </select>
            ) : (
              <span className="t-caption t-muted">{t("orders.ordermailSourceMailOrStandard")}</span>
            )
          }
          value={
            fromFile ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "6px" }}>
                {placeFields.map((field) => (
                  <div key={field} className="wl-field">
                    <label className="wl-field__label" htmlFor={`ordermail-${field}`}>
                      {t(`orders.mailField.${field}` as never)}
                    </label>
                    <ColumnSelect id={`ordermail-${field}`} value={fields[field] ?? ""} headers={headers} noneLabel={t("orders.ordermailSourceNone")} onChange={(value) => setField(field, value)} />
                  </div>
                ))}
              </div>
            ) : byText ? (
              <div className="wl-vstack" style={{ gap: "6px", alignItems: "stretch" }}>
                <div className="wl-field">
                  <label className="wl-field__label" htmlFor={`ordermail-${side}-rule-column`}>
                    {t("orders.ordermailRuleColumn")}
                  </label>
                  <ColumnSelect
                    id={`ordermail-${side}-rule-column`}
                    value={rule.header}
                    headers={headers}
                    noneLabel={t("orders.ordermailSourceNone")}
                    onChange={(header) => setRule(side, (now) => ({ ...now, header }))}
                  />
                </div>
                {rule.places.map((one, i) => (
                  <div key={i} className="wl-hstack" style={{ gap: "6px", alignItems: "center" }}>
                    <input
                      className="wl-input"
                      aria-label={t("orders.ordermailRuleContains")}
                      placeholder={t("orders.ordermailRuleContains")}
                      value={one.contains}
                      maxLength={60}
                      style={{ width: "140px" }}
                      onChange={(e) => setRulePlace(i, { contains: e.target.value })}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <LocationPicker id={`ordermail-${side}-rule-${i}`} ariaLabel={t("orders.ordermailRulePlace")} value={one.place} onChange={(place) => setRulePlace(i, { place })} />
                    </div>
                    <button
                      type="button"
                      className="wl-btn wl-btn--ghost wl-btn--sm"
                      disabled={rule.places.length === 1}
                      onClick={() => setRule(side, (now) => ({ ...now, places: now.places.filter((_, j) => j !== i) }))}
                    >
                      {t("common.remove")}
                    </button>
                  </div>
                ))}
                {rule.places.some((one) => one.place?.kind === "address") && <span className="t-caption" style={{ color: "var(--text-danger)" }}>{t("orders.ordermailPlaceBook")}</span>}
                <div>
                  <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" onClick={() => setRule(side, (now) => ({ ...now, places: [...now.places, { contains: "", place: null }] }))}>
                    {t("orders.ordermailRuleAdd")}
                  </button>
                </div>
                <span className="t-caption t-muted">{t("orders.ordermailRuleElse")}</span>
                {fixedPicker}
              </div>
            ) : (
              fixedPicker
            )
          }
          example={
            fromFile && first
              ? [first.input[side].place?.name, first.input[side].place?.city].filter(Boolean).join(", ") || null
              : byText && first
                ? countsByPlace(side)
                : null
          }
        />
        <SourceRow
          label={t(side === "load" ? "orders.ordermailLoadWhen" : "orders.ordermailUnloadWhen")}
          source={<span className="t-caption t-muted">{isList ? t("orders.ordermailSourceFile") : t("orders.ordermailSourceMail")}</span>}
          value={
            isList || side === "load" ? (
              <div className="wl-vstack" style={{ gap: "6px", alignItems: "stretch" }}>
                {isList && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                    <ColumnSelect id={`ordermail-${side}Date`} value={fields[`${side}Date`] ?? ""} headers={headers} noneLabel={t("orders.ordermailDateNone")} onChange={(value) => setField(`${side}Date`, value)} />
                    <ColumnSelect id={`ordermail-${side}Time`} value={fields[`${side}Time`] ?? ""} headers={headers} noneLabel={t("orders.ordermailTimeNone")} onChange={(value) => setField(`${side}Time`, value)} />
                  </div>
                )}
                {/* The loading day the mail does not name, beside the loading day itself. */}
                {side === "load" && (
                  <div className="wl-field">
                    <label className="wl-field__label" htmlFor="channel-load-rule">
                      {t(isList ? "orders.mailChannelLoadDay" : "orders.mailChannelLoadDayMail")}
                    </label>
                    <select id="channel-load-rule" className="wl-select" value={loadDateRule} onChange={(e) => setLoadDateRule(e.target.value as "" | "previous_working_day")}>
                      <option value="">{t(isList ? "orders.mailChannelLoadDayList" : "orders.mailChannelLoadDayFromMail")}</option>
                      <option value="previous_working_day">{t("orders.mailChannelLoadDayBefore")}</option>
                    </select>
                  </div>
                )}
              </div>
            ) : null
          }
          example={isList && first ? whenOfSide(first.input[side]) : null}
        />
        {sourced(`${side}Reference`, textInput(`ordermail-${side}-reference`, stop.reference, (reference) => setStop(side, { reference }), 100), Boolean(stop.reference.trim()))}
        {sourced(`${side}Instruction`, textInput(`ordermail-${side}-instruction`, stop.instruction, (instruction) => setStop(side, { instruction }), 500), Boolean(stop.instruction.trim()))}
        {shows(`${groupLabel}: ${t("orders.contactLabel")}`, Boolean(stop.contactName.trim() || stop.contactPhone.trim())) && (
          <SourceRow
            label={t("orders.contactLabel")}
            source={<span className="t-caption t-muted">{t("orders.ordermailSourceStandard")}</span>}
            value={
              <div className="wl-hstack" style={{ gap: "6px" }}>
                {textInput(`ordermail-${side}-contact`, stop.contactName, (contactName) => setStop(side, { contactName }), 100, t("orders.contactNameLabel"))}
                {textInput(`ordermail-${side}-phone`, stop.contactPhone, (contactPhone) => setStop(side, { contactPhone }), 50, t("orders.contactPhoneLabel"))}
              </div>
            }
            example={null}
          />
        )}
        {shows(`${groupLabel}: ${t("orders.ordermailStopRequirements")}`, stop.requirements.length > 0) && (
          <SourceRow
            label={t("orders.ordermailStopRequirements")}
            source={<span className="t-caption t-muted">{t("orders.ordermailSourceStandard")}</span>}
            value={<StopRequirementField id={`ordermail-${side}-requirements`} selected={stop.requirements} onChange={(requirements) => setStop(side, { requirements })} />}
            example={null}
          />
        )}
      </>
    );
  };

  // The loading metres of a list: per pallet, or per carrier where the list says the carrier.
  const loadingMetresList = (
    <div className="wl-vstack" style={{ gap: "6px", alignItems: "stretch" }}>
      <div className="wl-hstack" style={{ gap: "6px", alignItems: "center" }}>
        <input id="ordermail-per-pallet" className="wl-input t-mono" inputMode="decimal" style={{ width: "90px" }} value={perPallet} placeholder="0,4" onChange={(e) => setPerPallet(e.target.value)} />
        <span className="t-caption t-muted">{t("orders.ordermailPerPallet")}</span>
      </div>
      <div className="wl-field">
        <label className="wl-field__label" htmlFor="channel-carriers">
          {t("orders.mailChannelCarriers")}
        </label>
        <input id="channel-carriers" className="wl-input t-mono" value={carriers} placeholder="euro=0,4; blok=0,5" onChange={(e) => setCarriers(e.target.value)} />
      </div>
    </div>
  );

  // What the example comes to, before anything is made (as importers show a summary before they import): the rows, how
  // many become an order, how many are not for us and how many miss something; and the columns nothing reads.
  const ordersOfSample = rows.filter((row) => !row.filtered);
  const previewSummary = [
    t("orders.ordermailPreviewOrders", { count: ordersOfSample.length, rows: rows.length }),
    ...(rows.length > ordersOfSample.length ? [t("orders.ordermailPreviewSkipped", { count: rows.length - ordersOfSample.length })] : []),
    ...(ordersOfSample.some((row) => row.problems.length > 0) ? [t("orders.ordermailPreviewProblems", { count: ordersOfSample.filter((row) => row.problems.length > 0).length })] : []),
  ].join(" · ");
  const usedColumns = new Set<string>([
    ...Object.entries(fields).flatMap(([field, header]) =>
      header && !(["load", "unload"] as const).some((side) => !placeFromFile[side] && (PLACE_FIELDS[side] as readonly string[]).includes(field)) ? [header] : [],
    ),
    ...(filterHeader ? [filterHeader] : []),
    ...usedRules.map((rule) => rule.header),
  ]);
  const unusedColumns = (sampleList?.header ?? []).filter((header) => header && !usedColumns.has(header));
  /** The place every row of the example shares on this side, said once above the column instead of on every row. */
  const sharedPlace = (side: "load" | "unload") => {
    const places = new Set(rows.map((row) => placeOfRow(row, side)));
    return places.size === 1 ? ([...places][0] ?? null) : undefined;
  };
  const sharedLoad = sharedPlace("load");
  const sharedUnload = sharedPlace("unload");

  return (
    <form
      className="wl-card wl-vstack"
      style={{ padding: "var(--space-4) var(--space-5)", gap: "14px", alignItems: "stretch" }}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) save();
      }}
    >
      <span className="t-body" style={{ fontWeight: "var(--fw-semibold)" }}>
        {current ? t("orders.mailChannelEditTitle", { name: current.name }) : t("orders.mailChannelNewTitle")}
      </span>
      {/* One column of numbered steps, in the order the work goes (Camiel, 26-10-2026: "het staat allemaal op zo'n hoop";
          as importers do it: first the file, then the mapping): which mail, the example as WLX reads it — every row with
          its places, which rows become an order —, the order per field, what every order gets. */}
      <div className="wl-vstack" style={{ gap: "var(--space-4)", alignItems: "stretch", maxWidth: "1100px" }}>
        <Step n={1} title={t("orders.ordermailStepMail")} help={t("orders.ordermailStepMailHelp")}>
          {/* How WLX reads it decides everything below, so it comes first (Camiel, 27-10-2026). */}
          <div className="wl-field">
            <span className="wl-field__label">{t("orders.mailChannelReadAs")}</span>
            <div className="wl-segment" role="group" aria-label={t("orders.mailChannelReadAs")} style={{ alignSelf: "flex-start" }}>
              {(["list", "mail"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={readAs === option}
                  className={`wl-segment__btn${readAs === option ? " is-active" : ""}`}
                  onClick={() => setReadAs(option)}
                >
                  {t(option === "list" ? "orders.mailChannelReadAsList" : "orders.mailChannelReadAsMail")}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px 12px" }}>
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="channel-name">
                {t("orders.listTemplateName")}
              </label>
              <input id="channel-name" className="wl-input" value={name} maxLength={80} placeholder={t("orders.mailChannelNamePlaceholder")} onChange={(e) => setName(e.target.value)} />
            </div>
            {!current && !givenParty && (
              <div className="wl-field">
                <label className="wl-field__label" htmlFor="channel-party">
                  {t("orders.mailChannelCustomer")}
                </label>
                <CustomerPicker id="channel-party" value={party} onChange={setParty} />
              </div>
            )}
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="channel-senders">
                {t("orders.mailChannelSenders")}
              </label>
              <input id="channel-senders" className="wl-input" value={senders} placeholder="transport@klant.nl, @klant.nl" onChange={(e) => setSenders(e.target.value)} />
            </div>
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="channel-subject">
                {t("orders.mailChannelSubjectFilter")}
              </label>
              <input id="channel-subject" className="wl-input" value={subjectFilter} maxLength={120} placeholder={t("orders.mailChannelSubjectFilterPlaceholder")} onChange={(e) => setSubjectFilter(e.target.value)} />
            </div>
          </div>
          <label className="wl-hstack" style={{ gap: "8px", alignItems: "center" }}>
            <input type="checkbox" checked={autoIntake} onChange={(e) => setAutoIntake(e.target.checked)} />
            <span className="t-body-sm">{t("orders.mailChannelAutoIntake")}</span>
            <span className="t-caption t-muted">{t("orders.mailChannelAutoIntakeHelp")}</span>
          </label>
        </Step>

        {isList && (
          <Step n={2} title={t("orders.ordermailSampleTitle")} help={t("orders.mailChannelColumnsHelp")}>
            <div className="wl-vstack" style={{ gap: "6px", alignItems: "stretch" }}>
              <select
                id="channel-sample"
                aria-label={t("orders.ordermailSampleTitle")}
                className="wl-select"
                value={sampleId}
                disabled={partyId === null}
                onChange={(e) => {
                  setSampleId(e.target.value);
                  setSampleKey(null);
                  setFileSample(null);
                  setFileNote(null);
                }}
              >
                <option value="">{partyId === null ? t("orders.mailChannelSampleNoParty") : t("orders.mailChannelSampleNone")}</option>
                {(samples.data ?? []).map((one) => (
                  <option key={one.id} value={one.id}>
                    {[one.receivedAt ? formatActivityDate(one.receivedAt.slice(0, 10)) : null, one.subject || "—", one.fromName ?? one.from].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </select>
              <label className="wl-hstack t-caption t-muted" style={{ gap: "8px", alignItems: "center" }} htmlFor="channel-sample-file">
                {t("orders.mailChannelSampleFile")}
                <input id="channel-sample-file" type="file" accept=".xlsx,.xlsm,.csv,.pdf" className="t-body-sm" style={{ minWidth: 0 }} onChange={(e) => chooseFile(e.target.files?.[0])} />
              </label>
            </div>
            {sampleData && sampleData.lists.length > 1 && (
              <select id="channel-sample-list" aria-label={t("orders.mailList")} className="wl-select" value={sampleList?.key ?? ""} onChange={(e) => setSampleKey(e.target.value)}>
                {sampleData.lists.map((one) => (
                  <option key={one.key} value={one.key}>
                    {one.label || t("orders.mailBodyTable")} ({t("orders.mailRowCount", { count: one.rows.length })})
                  </option>
                ))}
              </select>
            )}
            {samples.data && samples.data.length === 0 && !fileSample && <span className="t-caption t-muted">{t("orders.mailChannelSampleEmpty")}</span>}
            {fileNote && <span className="t-caption t-muted">{fileNote}</span>}
            {sampleData && sampleData.lists.length === 0 && <span className="t-caption t-muted">{t("orders.mailNoList")}</span>}
            {/* Which rows become an order, and what their reference starts with: right above the rows they act on. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "10px 12px" }}>
              <div className="wl-field">
                <label className="wl-field__label" htmlFor="channel-filter-header">
                  {t("orders.mailChannelFilter")}
                </label>
                <div className="wl-hstack" style={{ gap: "6px" }}>
                  <select id="channel-filter-header" className="wl-select" value={filterHeader} onChange={(e) => setFilterHeader(e.target.value)} style={{ minWidth: 0, flex: 1 }}>
                    <option value="">{t("orders.mailChannelFilterNone")}</option>
                    {[...new Set([...headers, ...(filterHeader ? [filterHeader] : [])])].map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                  {filterHeader && (
                    <input
                      className="wl-input"
                      aria-label={t("orders.mailChannelFilterText")}
                      value={filterText}
                      placeholder={t("orders.mailChannelFilterText")}
                      style={{ width: "110px" }}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  )}
                </div>
              </div>
              <div className="wl-field">
                <label className="wl-field__label" htmlFor="channel-prefix">
                  {t("orders.repeatPrefix")}
                </label>
                <input id="channel-prefix" className="wl-input" value={prefix} maxLength={40} placeholder={t("orders.listTemplatePrefixPlaceholder")} onChange={(e) => setPrefix(e.target.value)} />
              </div>
            </div>

            {rows.length > 0 && (
              <>
                <span className="t-body-sm">{previewSummary}</span>
                <div className="wl-table-wrap" style={{ maxHeight: "none" }}>
                  <table className="wl-table wl-table--compact">
                    <thead>
                      <tr>
                        <th>{t("orders.repeatColReference")}</th>
                        <th>
                          {t("orders.repeatColLoad")}
                          {sharedLoad !== undefined && <div className="t-caption t-muted">{sharedLoad ?? t("orders.ordermailNoPlace")}</div>}
                        </th>
                        <th>
                          {t("orders.repeatColUnload")}
                          {sharedUnload !== undefined && <div className="t-caption t-muted">{sharedUnload ?? t("orders.ordermailNoPlace")}</div>}
                        </th>
                        <th style={{ textAlign: "right" }}>{t("orders.ordermailColPalletsMetres")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const note = row.filtered ? t("orders.mailRowFiltered") : row.problems.map((problem) => t(`orders.mailProblem.${problem}` as never)).join(" · ");
                        return (
                          <tr key={row.index} style={row.filtered ? { color: "var(--text-tertiary)" } : undefined}>
                            <td>
                              <span className="t-mono">{row.input.customerReference || "—"}</span>
                              {note && (
                                <div className="t-caption" style={!row.filtered ? { color: "var(--text-danger)" } : undefined}>
                                  {note}
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: "normal" }}>
                              {whenOfSide(row.input.load)}
                              {sharedLoad === undefined && <div className="t-caption t-muted">{placeOfRow(row, "load") ?? t("orders.ordermailNoPlace")}</div>}
                            </td>
                            <td style={{ whiteSpace: "normal" }}>
                              {whenOfSide(row.input.unload)}
                              {sharedUnload === undefined && <div className="t-caption t-muted">{placeOfRow(row, "unload") ?? t("orders.ordermailNoPlace")}</div>}
                            </td>
                            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              {row.input.pallets ?? "—"}
                              <div className="t-caption t-muted">{metresText(row.input.loadingMeters) || "—"}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {unusedColumns.length > 0 && <span className="t-caption t-muted">{t("orders.ordermailUnusedColumns", { columns: unusedColumns.join(" · ") })}</span>}
              </>
            )}
          </Step>
        )}

        <Step n={isList ? 3 : 2} title={t("orders.ordermailStepOrder")} help={isList ? t("orders.ordermailOrderHelpList") : t("orders.ordermailOrderHelpMail")}>
          <Group title={t("orders.mailGroupOrder")}>
            {isList && sourced("reference", null)}
            {isList && sourced("name", null, true)}
            {shows(t("orders.priorityLabel"), standard.priority !== "regular") && (
              <SourceRow
                label={t("orders.priorityLabel")}
                source={<span className="t-caption t-muted">{t("orders.ordermailSourceStandard")}</span>}
                value={
                  <div className="wl-segment" role="group" aria-label={t("orders.priorityLabel")}>
                    {ORDER_PRIORITIES.map((option) => (
                      <button
                        key={option}
                        type="button"
                        aria-pressed={standard.priority === option}
                        className={`wl-segment__btn${standard.priority === option ? " is-active" : ""}`}
                        onClick={() => setStandard((now) => ({ ...now, priority: option }))}
                      >
                        {t(PRIORITY_LABEL_KEYS[option] as never)}
                      </button>
                    ))}
                  </div>
                }
                example={null}
              />
            )}
            {sourced("description", textInput("ordermail-description", standard.description, (description) => setStandard((now) => ({ ...now, description }))), Boolean(standard.description.trim()))}
            {isList && sourced("pallets", null, true)}
            {isList && sourced("carrier", null)}
            {isList && sourced("loadingMeters", loadingMetresList, true)}
            {!isList &&
              sourced(
                "loadingMeters",
                <input
                  id="ordermail-loading-meters"
                  className="wl-input t-mono"
                  inputMode="decimal"
                  style={{ width: "90px" }}
                  value={standard.loadingMeters}
                  placeholder="13,6"
                  onChange={(e) => setStandard((now) => ({ ...now, loadingMeters: e.target.value }))}
                />,
                true,
              )}
            {isList && sourced("weightKg", null)}
            {shows(t("orders.ordermailRequirements"), standard.requirementIds.length > 0) && (
              <SourceRow
                label={t("orders.ordermailRequirements")}
                source={<span className="t-caption t-muted">{t("orders.ordermailSourceStandard")}</span>}
                value={<RequirementField id="ordermail-requirements" selected={standard.requirementIds} known={known} onChange={(requirementIds) => setStandard((now) => ({ ...now, requirementIds }))} />}
                example={null}
              />
            )}
            {sourced("remark", textInput("ordermail-remark", standard.remark, (remark) => setStandard((now) => ({ ...now, remark })), 500), Boolean(standard.remark.trim()))}
            {shows(t("orders.ordermailShipper"), shipper !== null) && (
              <SourceRow
                label={t("orders.ordermailShipper")}
                source={<span className="t-caption t-muted">{t("orders.ordermailSourceStandard")}</span>}
                value={
                  <div className="wl-vstack" style={{ gap: "2px", alignItems: "stretch" }}>
                    <LocationPicker
                      id="ordermail-shipper"
                      ariaLabel={t("orders.ordermailShipper")}
                      value={shipper}
                      onChange={(place) => {
                        setShipper(place);
                        setShipperChanged(true);
                      }}
                    />
                    <span className="t-caption t-muted">{t("orders.ordermailShipperHelp")}</span>
                  </div>
                }
                example={null}
              />
            )}
            {!isList && <span className="t-caption t-muted">{t("orders.ordermailMailFields")}</span>}
          </Group>
          <Group title={t("orders.mailGroupLoad")}>{stopRows("load")}</Group>
          <Group title={t("orders.mailGroupUnload")}>{stopRows("unload")}</Group>
          {(allFields || hiddenFields.length > 0) && (
            <div className="wl-vstack" style={{ gap: "2px", alignItems: "flex-start" }}>
              <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" onClick={() => setAllFields((now) => !now)}>
                {allFields ? t("orders.ordermailFewerFields") : t("orders.ordermailMoreFields", { count: hiddenFields.length })}
              </button>
              {!allFields && <span className="t-caption t-muted">{hiddenFields.join(" · ")}</span>}
            </div>
          )}
        </Step>

        <Step n={isList ? 4 : 3} title={t("orders.ordermailStepEvery")} help={t("orders.ordermailNoHistory")}>
          <label className="wl-hstack" style={{ gap: "8px", alignItems: "center" }}>
            <input type="checkbox" checked={provisional} onChange={(e) => setProvisional(e.target.checked)} />
            <span className="t-body-sm">{t("orders.repeatProvisional")}</span>
            <span className="t-caption t-muted">{t("orders.mailChannelProvisionalHelp")}</span>
          </label>
          <label className="wl-hstack" style={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <input type="checkbox" checked={noInvoice} onChange={(e) => setNoInvoice(e.target.checked)} />
            <span className="t-body-sm">{t("orders.repeatNoInvoice")}</span>
            {noInvoice && (
              <input className="wl-input" aria-label={t("orders.repeatNoInvoiceReason")} value={noInvoiceReason} maxLength={200} style={{ width: "240px" }} onChange={(e) => setNoInvoiceReason(e.target.value)} />
            )}
          </label>
          {/* Scoretrace, plainly (Camiel, 25-10-2026: "Het moet kraakhelder en duidelijk zijn en visueel goed zichtbaar"). */}
          <div
            className="wl-vstack"
            style={{
              gap: "6px",
              alignItems: "stretch",
              border: `1px solid ${proposeWarehouse && !isList ? "var(--teal-600)" : "var(--border-default)"}`,
              borderLeftWidth: "4px",
              borderRadius: "var(--radius-md)",
              background: "var(--surface-base)",
              padding: "var(--space-3)",
            }}
          >
            <span className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
              {t("orders.ordermailWmsTitle")}
            </span>
            <label className="wl-hstack" style={{ gap: "8px", alignItems: "center" }}>
              <input type="checkbox" checked={proposeWarehouse} disabled={isList} onChange={(e) => setProposeWarehouse(e.target.checked)} />
              <span className="t-body-sm">{t("orders.ordermailWmsToggle")}</span>
            </label>
            <span className="t-body-sm">
              {isList
                ? t("orders.ordermailWmsList")
                : !proposeWarehouse
                  ? t("orders.ordermailWmsOff")
                  : loadWarehouse
                    ? t("orders.ordermailWmsOutbound", { warehouse: loadWarehouse.name, customer: customerName })
                    : unloadWarehouse
                      ? t("orders.ordermailWmsInbound", { warehouse: unloadWarehouse.name, customer: customerName })
                      : t("orders.ordermailWmsNoWarehouse")}
            </span>
            {!isList && proposeWarehouse && <span className="t-caption t-muted">{t("orders.ordermailWmsLines")}</span>}
          </div>
        </Step>
      </div>
      {error && (
        <div className="wl-alert wl-alert--danger" role="alert">
          <span>{error}</span>
        </div>
      )}
      {partyId === null && <span className="t-caption t-muted">{t("orders.mailChannelPartyRequired")}</span>}
      <div className="wl-hstack" style={{ gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        <button type="submit" className={current ? "wl-btn wl-btn--primary" : "wl-btn wl-btn--accent"} disabled={isPending || !ready}>
          {current ? t("orders.listTemplateSave") : t("orders.mailChannelCreate")}
        </button>
        <button type="button" className="wl-btn wl-btn--ghost" disabled={isPending} onClick={close}>
          {t("common.cancel")}
        </button>
        {isList && rows.length > 0 && <span className="t-caption">{previewSummary}</span>}
        {dirty && <span className="t-caption t-muted">{t("orders.ordermailUnsaved")}</span>}
        <span className="wl-spacer" />
        {current &&
          (deleting ? (
            <>
              <span className="t-caption">{t("orders.mailChannelDeleteConfirm", { name: current.name })}</span>
              <button type="button" className="wl-btn wl-btn--danger wl-btn--sm" disabled={isPending} onClick={remove}>
                {t("orders.listTemplateDelete")}
              </button>
              <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => setDeleting(false)}>
                {t("common.cancel")}
              </button>
            </>
          ) : (
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => setDeleting(true)}>
              {t("orders.listTemplateDelete")}
            </button>
          ))}
      </div>
      {(blocker.status === "blocked" || asking) && (
        <LeaveOrdermail
          saving={isPending}
          canSave={ready}
          onSave={() =>
            startTransition(async () => {
              const saved = await persist();
              if (!saved) {
                setAsking(false);
                if (blocker.status === "blocked") blocker.reset();
                return;
              }
              leaving.current = true;
              setAsking(false);
              if (blocker.status === "blocked") blocker.proceed();
              onSaved(saved);
            })
          }
          onDiscard={() => {
            leaving.current = true;
            setAsking(false);
            if (blocker.status === "blocked") blocker.proceed();
            else onClose();
          }}
          onStay={() => {
            setAsking(false);
            if (blocker.status === "blocked") blocker.reset();
          }}
        />
      )}
    </form>
  );
}

/** Leaving an ordermail with changes: save them, let them go, or stay. Saving comes first: the work is the point. */
function LeaveOrdermail({ saving, canSave, onSave, onDiscard, onStay }: { saving: boolean; canSave: boolean; onSave: () => void; onDiscard: () => void; onStay: () => void }) {
  const { t } = useTranslation();
  const saveRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    saveRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onStay();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onStay]);
  return (
    <div
      className="wl-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onStay();
      }}
    >
      <div role="alertdialog" aria-modal="true" aria-labelledby="ordermail-leave-title" aria-describedby="ordermail-leave-body" className="wl-modal">
        <div className="wl-modal__header">
          <span id="ordermail-leave-title" className="wl-modal__title">
            {t("orders.ordermailLeaveTitle")}
          </span>
        </div>
        <div className="wl-modal__body">
          <div className="wl-vstack" style={{ gap: "var(--space-4)", alignItems: "stretch", padding: "var(--space-2) var(--space-5) var(--space-4)" }}>
            <p id="ordermail-leave-body" className="t-body-sm" style={{ margin: 0 }}>
              {canSave ? t("orders.ordermailLeaveBody") : t("orders.ordermailLeaveBodyIncomplete")}
            </p>
            <div className="wl-hstack" style={{ gap: "var(--space-2_5)", justifyContent: "flex-end" }}>
              <button type="button" className="wl-btn wl-btn--ghost" disabled={saving} onClick={onStay}>
                {t("orders.ordermailLeaveStay")}
              </button>
              <button type="button" className="wl-btn wl-btn--secondary" disabled={saving} onClick={onDiscard}>
                {t("orders.ordermailLeaveDiscard")}
              </button>
              {canSave && (
                <button ref={saveRef} type="button" className="wl-btn wl-btn--primary" disabled={saving} onClick={onSave}>
                  {saving ? t("common.loading") : t("orders.listTemplateSave")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** One step of the ordermail, numbered: its title, one line of help, and its fields. */
function Step({ n, title, help, children }: { n: number; title: string; help: string; children: ReactNode }) {
  return (
    <section className="wl-vstack" style={{ gap: "10px", alignItems: "stretch", borderTop: "1px solid var(--border-subtle)", paddingTop: "var(--space-3)" }}>
      <div className="wl-vstack" style={{ gap: "2px", alignItems: "stretch" }}>
        <span className="wl-hstack t-body-sm" style={{ gap: "8px", alignItems: "baseline", fontWeight: "var(--fw-semibold)" }}>
          <span className="t-mono t-muted">{n}</span>
          {title}
        </span>
        <span className="t-caption t-muted">{help}</span>
      </div>
      {children}
    </section>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="wl-vstack" style={{ gap: "6px", alignItems: "stretch" }}>
      <span className="t-caption t-muted" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {title}
      </span>
      {children}
    </div>
  );
}

/** One field of the example order: its name, where its value comes from, the standard when that is it, and what the example says. */
function SourceRow({ label, source, value, example }: { label: string; source: ReactNode; value: ReactNode | null; example: string | null }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(100px, 130px) minmax(140px, 180px) minmax(0, 1fr)", gap: "8px", alignItems: "center" }}>
      <span className="t-body-sm">{label}</span>
      <div>{source}</div>
      <div className="wl-vstack" style={{ gap: "2px", alignItems: "stretch" }}>
        {value}
        {example !== null && <span className="t-caption t-mono">{t("orders.ordermailExample", { value: example || "—" })}</span>}
      </div>
    </div>
  );
}

/** The column a field comes from, or none (then the standard, where the field has one). */
function ColumnSelect({ id, value, headers, noneLabel, onChange }: { id: string; value: string; headers: string[]; noneLabel: string; onChange: (header: string) => void }) {
  const { t } = useTranslation();
  const choices = [...new Set([...headers, ...(value ? [value] : [])])];
  return (
    <select id={id} className="wl-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{noneLabel}</option>
      {choices.length > 0 && (
        <optgroup label={t("orders.ordermailSourceFile")}>
          {choices.map((header) => (
            <option key={header} value={header}>
              {t("orders.ordermailColumn", { header })}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}
